import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, X, Check, Filter, Building2, GraduationCap, BookOpen, Calendar, Layers } from 'lucide-react';
import { type StudentFilters } from '@/services/userService';
import { formatAcademicYearName } from '@/utils/academicYear';

interface FilterOption {
  value: string;
  label: string;
  count?: number;
}

interface FilterDropdownProps {
  label: string;
  icon: React.ElementType;
  options: FilterOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
}

export const FilterDropdown: React.FC<FilterDropdownProps> = ({
  label,
  icon: Icon,
  options,
  selectedValues,
  onChange,
  placeholder = 'Tous',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter((v) => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const clearSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  };

  const displayText = selectedValues.length === 0
    ? placeholder
    : selectedValues.length === 1
      ? options.find((o) => o.value === selectedValues[0])?.label || selectedValues[0]
      : `${selectedValues.length} sélectionnés`;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 text-sm border rounded-lg transition-all min-w-[140px] ${
          selectedValues.length > 0
            ? 'border-primary-300 bg-primary-50 text-primary-700'
            : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
        }`}
      >
        <Icon size={16} className={selectedValues.length > 0 ? 'text-primary-600' : 'text-gray-400'} />
        <span className="truncate flex-1 text-left">{displayText}</span>
        {selectedValues.length > 0 ? (
          <button
            onClick={clearSelection}
            className="p-0.5 hover:bg-primary-200 rounded-full"
          >
            <X size={14} />
          </button>
        ) : (
          <ChevronDown size={16} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        )}
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-1 max-h-64 overflow-y-auto">
          <div className="px-3 py-2 border-b border-gray-100">
            <span className="text-xs font-medium text-gray-500 uppercase">{label}</span>
          </div>
          {options.length === 0 ? (
            <div className="px-3 py-4 text-sm text-gray-500 text-center">
              Aucune option disponible
            </div>
          ) : (
            options.map((option) => (
              <button
                key={option.value}
                onClick={() => toggleOption(option.value)}
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-left"
              >
                <div
                  className={`w-4 h-4 rounded border flex items-center justify-center ${
                    selectedValues.includes(option.value)
                      ? 'bg-primary-600 border-primary-600'
                      : 'border-gray-300'
                  }`}
                >
                  {selectedValues.includes(option.value) && (
                    <Check size={12} className="text-white" />
                  )}
                </div>
                <span className="text-sm text-gray-700 truncate flex-1">{option.label}</span>
                {option.count !== undefined && (
                  <span className="text-xs text-gray-400">({option.count})</span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

interface StudentFiltersPanelProps {
  filters: StudentFilters;
  onFiltersChange: (filters: StudentFilters) => void;
  studentData: Array<{
    codeFiliereInscrit?: string | null;
    filiereInscrit?: string | null;
    diplomeInscrit?: string | null;
    niveauFormation?: number | null;
    campus?: { id: string; name: string } | null;
    academicYear?: { id: string; name: string } | null;
    program?: { id: string; code: string; name: string; durationYears: number } | null;
    currentSemester?: number | null;
  }>;
  availableAcademicYears?: Array<{ id: string; name: string }>; // Academic years from the database
  /** Headcount per academic year id, read from the enrolment history. */
  academicYearCounts?: Record<string, number>;
}

export const StudentFiltersPanel: React.FC<StudentFiltersPanelProps> = ({
  filters,
  onFiltersChange,
  studentData,
  availableAcademicYears = [],
  academicYearCounts,
}) => {
  // Extract unique values from student data with counts
  const extractUniqueWithCount = <T extends string | number>(
    data: Array<T | null | undefined>
  ): Map<T, number> => {
    const countMap = new Map<T, number>();
    data.forEach((value) => {
      if (value !== null && value !== undefined) {
        countMap.set(value, (countMap.get(value) || 0) + 1);
      }
    });
    return countMap;
  };

  // Get filières/programs options - show programs that students are enrolled in
  const filiereOptions: FilterOption[] = React.useMemo(() => {
    // Build program map from student data (code -> {name, count})
    const programMap = new Map<string, { name: string; count: number }>();

    studentData.forEach((s) => {
      // Use codeFiliereInscrit if available, otherwise use program code
      const code = s.codeFiliereInscrit || s.program?.code;
      const name = s.filiereInscrit || s.program?.name;

      if (code && name) {
        const existing = programMap.get(code);
        if (existing) {
          existing.count += 1;
        } else {
          programMap.set(code, { name, count: 1 });
        }
      }
    });

    // Convert to options array
    return Array.from(programMap.entries())
      .map(([code, { name, count }]) => ({
        value: code,
        label: name,
        count,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [studentData]);

  // Get diplôme options - this filter is hidden if no students have diplomeInscrit set
  const diplomeOptions: FilterOption[] = React.useMemo(() => {
    // Count students enrolled in each diplôme
    const enrolledDiplomes = extractUniqueWithCount(
      studentData.map((s) => s.diplomeInscrit)
    );

    // Only show diplômes that have enrolled students
    return Array.from(enrolledDiplomes.entries())
      .map(([diplome, count]) => ({
        value: diplome,
        label: diplome,
        count,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [studentData]);

  // Get niveau options - show levels that students are at (based on niveauFormation or currentSemester)
  const niveauOptions: FilterOption[] = React.useMemo(() => {
    // Count students at each level
    const enrolledNiveaux = new Map<number, number>();

    studentData.forEach((s) => {
      // Use niveauFormation if available, otherwise calculate from currentSemester
      const niveau = s.niveauFormation || (s.currentSemester ? Math.ceil(s.currentSemester / 2) : null);

      if (niveau) {
        enrolledNiveaux.set(niveau, (enrolledNiveaux.get(niveau) || 0) + 1);
      }
    });

    // Convert to options array sorted by level
    return Array.from(enrolledNiveaux.entries())
      .map(([niveau, count]) => ({
        value: niveau.toString(),
        label: `Niveau ${niveau}`,
        count,
      }))
      .sort((a, b) => Number(a.value) - Number(b.value));
  }, [studentData]);

  // Get academic year options - use availableAcademicYears with counts from student data
  const academicYearOptions: FilterOption[] = React.useMemo(() => {
    // Count students per academic year
    const studentCountByYear = new Map<string, number>();
    studentData.forEach((s) => {
      if (s.academicYear) {
        studentCountByYear.set(s.academicYear.id, (studentCountByYear.get(s.academicYear.id) || 0) + 1);
      }
    });

    // If availableAcademicYears is provided, use it; otherwise fall back to studentData
    if (availableAcademicYears.length > 0) {
      return availableAcademicYears
        .map((year) => ({
          value: year.id,
          label: formatAcademicYearName(year.name),
          // Counts from the enrolment history when the server provides them:
          // the loaded page only knows where each student stands today, which
          // empties a year as soon as its cohort has been promoted.
          count: academicYearCounts?.[year.id] ?? studentCountByYear.get(year.id) ?? 0,
        }))
        .sort((a, b) => b.label.localeCompare(a.label)); // Most recent first
    }

    // Fallback: extract from student data
    const yearMap = new Map<string, { name: string; count: number }>();
    studentData.forEach((s) => {
      if (s.academicYear) {
        const existing = yearMap.get(s.academicYear.id);
        if (existing) {
          existing.count += 1;
        } else {
          yearMap.set(s.academicYear.id, { name: s.academicYear.name, count: 1 });
        }
      }
    });

    return Array.from(yearMap.entries())
      .map(([id, { name, count }]) => ({
        value: id,
        label: formatAcademicYearName(name),
        count,
      }))
      .sort((a, b) => b.label.localeCompare(a.label)); // Most recent first
  }, [studentData, availableAcademicYears, academicYearCounts]);

  // Get campus options from enrolled students (with count)
  const campusOptions: FilterOption[] = React.useMemo(() => {
    const campusMap = new Map<string, { name: string; count: number }>();

    studentData.forEach((s) => {
      if (s.campus) {
        const existing = campusMap.get(s.campus.id);
        if (existing) {
          existing.count += 1;
        } else {
          campusMap.set(s.campus.id, { name: s.campus.name, count: 1 });
        }
      }
    });

    return Array.from(campusMap.entries())
      .map(([id, { name, count }]) => ({
        value: id,
        label: name,
        count,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [studentData]);

  // Check if any filters are active
  const hasActiveFilters =
    (filters.codeFiliereInscrit && filters.codeFiliereInscrit.length > 0) ||
    (filters.diplomeInscrit && filters.diplomeInscrit.length > 0) ||
    (filters.niveauFormation && filters.niveauFormation.length > 0) ||
    (filters.academicYearId && filters.academicYearId.length > 0) ||
    (filters.campusId && filters.campusId.length > 0);

  const clearAllFilters = () => {
    onFiltersChange({
      codeFiliereInscrit: undefined,
      diplomeInscrit: undefined,
      niveauFormation: undefined,
      academicYearId: undefined,
      campusId: undefined,
    });
  };

  // Debug: log filter data (remove in production)
  React.useEffect(() => {
    console.log('[StudentFiltersPanel] studentData count:', studentData.length);
    console.log('[StudentFiltersPanel] Students with program:',
      studentData.filter(s => s.program).length);
    console.log('[StudentFiltersPanel] Students with currentSemester:',
      studentData.filter(s => s.currentSemester).length);
    console.log('[StudentFiltersPanel] Students with codeFiliereInscrit:',
      studentData.filter(s => s.codeFiliereInscrit).length);
    console.log('[StudentFiltersPanel] Filter options - filières:', filiereOptions.length,
      'diplômes:', diplomeOptions.length, 'niveaux:', niveauOptions.length);
    if (studentData.length > 0) {
      console.log('[StudentFiltersPanel] Sample student:', studentData[0]);
    }
  }, [studentData, filiereOptions, diplomeOptions, niveauOptions]);

  return (
    <div className="space-y-3">
      {/* Filters Row */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 text-sm text-gray-500 mr-2">
          <Filter size={16} />
          <span>Filtres:</span>
        </div>

        {/* Filière Filter - Always show if filières exist in nomenclature */}
        {filiereOptions.length > 0 && (
          <FilterDropdown
            label="Filière"
            icon={GraduationCap}
            options={filiereOptions}
            selectedValues={filters.codeFiliereInscrit || []}
            onChange={(values) =>
              onFiltersChange({ ...filters, codeFiliereInscrit: values.length > 0 ? values : undefined })
            }
            placeholder="Filière"
          />
        )}

        {/* Diplôme Filter - Always show if diplômes exist in nomenclature */}
        {diplomeOptions.length > 0 && (
          <FilterDropdown
            label="Diplôme"
            icon={BookOpen}
            options={diplomeOptions}
            selectedValues={filters.diplomeInscrit || []}
            onChange={(values) =>
              onFiltersChange({ ...filters, diplomeInscrit: values.length > 0 ? values : undefined })
            }
            placeholder="Diplôme"
          />
        )}

        {/* Niveau Filter - Always show levels */}
        {niveauOptions.length > 0 && (
          <FilterDropdown
            label="Niveau d'étude"
            icon={Layers}
            options={niveauOptions}
            selectedValues={(filters.niveauFormation || []).map(String)}
            onChange={(values) =>
              onFiltersChange({
                ...filters,
                niveauFormation: values.length > 0 ? values.map(Number) : undefined,
              })
            }
            placeholder="Niveau"
          />
        )}

        {/* Année Académique Filter */}
        {academicYearOptions.length > 0 && (
          <FilterDropdown
            label="Année académique"
            icon={Calendar}
            options={academicYearOptions}
            selectedValues={filters.academicYearId || []}
            onChange={(values) =>
              onFiltersChange({ ...filters, academicYearId: values.length > 0 ? values : undefined })
            }
            placeholder="Année"
          />
        )}

        {/* Campus Filter */}
        {campusOptions.length > 0 && (
          <FilterDropdown
            label="Campus"
            icon={Building2}
            options={campusOptions}
            selectedValues={filters.campusId || []}
            onChange={(values) =>
              onFiltersChange({ ...filters, campusId: values.length > 0 ? values : undefined })
            }
            placeholder="Campus"
          />
        )}

        {/* Clear All Button */}
        {hasActiveFilters && (
          <button
            onClick={clearAllFilters}
            className="flex items-center gap-1 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <X size={16} />
            <span>Effacer tout</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default StudentFiltersPanel;
